import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DropdownOption, DropdownPanel, RelativeWrap, SelectButton } from './style';

interface FinancialOptionDropdownProps<T extends string> {
  value: T | undefined;
  options: ReadonlyArray<{ value: T; label: string }>;
  getLabel?: (value: T | undefined) => string;
  onChange: (value: T) => void;
}

function FinancialOptionDropdown<T extends string>({
  value,
  options,
  getLabel,
  onChange,
}: FinancialOptionDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayLabel =
    getLabel?.(value) ??
    options.find((option) => option.value === value)?.label ??
    options[0]?.label ??
    '—';

  return (
    <RelativeWrap ref={ref}>
      <SelectButton type="button" onClick={() => setOpen((current) => !current)}>
        {displayLabel}
        <ChevronDown size={14} />
      </SelectButton>
      {open && (
        <DropdownPanel>
          {options.map((option) => (
            <DropdownOption
              key={option.value}
              type="button"
              $active={value === option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </DropdownOption>
          ))}
        </DropdownPanel>
      )}
    </RelativeWrap>
  );
}

export default FinancialOptionDropdown;
